import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/RestoreFromTrash";
import {
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CircleFadingArrowUpIcon,
} from "lucide-react";
import * as React from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import type { Control } from "react-hook-form";

import { AlertDialog } from "@/shared/components";
import type {
  FeatureFlagRecord,
  RolloutStatus,
} from "@/shared/domain/featureFlags";
import {
  useAddFeatureFlagRolloutGroups,
  useCreateFeatureFlag,
  useDeleteFeatureFlag,
  useDeletedFeatureFlags,
  useFeatureFlags,
  useRemoveFeatureFlagRolloutGroups,
  useRestoreFeatureFlag,
  useUpdateFeatureFlag,
} from "@/shared/domain/featureFlags/hooks";
import { useUserGroups } from "@/shared/domain/userGroups/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  buildColumnsForPreferences,
  cellStyles,
  createColumnHelper,
  deriveLoadingState,
  renderDashOrValue,
  useTable,
  type ColDef,
  type ICellRendererParams,
  type Table,
} from "@/shared/foundation/primitives/Table";
import { RobotoConflictException } from "@/shared/http";

import styles from "./FeatureFlagsPanel.module.css";

const explanation =
  "Feature Flags allow us to progressively roll out a change to a subset of our users before " +
  "rolling out to everyone.\n\n" +
  "Feature flag names should be unique. They should not be deleted until after the flag is completely enabled for all " +
  "users (or deprecated and disabled for all users), all references to the flags have been deleted from the code, " +
  "and there is no remaining risk of a rollback restoring the flags into production code.";

const featureFlagOrder: RolloutStatus[] = ["NoUsers", "Partial", "AllUsers"];

const ROLLOUT_LABEL_GROUP_THRESHOLD = 2;

const columnHelper = createColumnHelper<FeatureFlagRecord>();

const DEFAULT_VISIBLE_COLUMN_IDS = ["name", "description", "rollout"];

const DELETED_VISIBLE_COLUMN_IDS = ["name", "description", "rollout"];

interface UserGroupOption {
  label: string;
  value: string;
}

interface FeatureFlagFormValues {
  name: string;
  description: string;
  rolloutStatus: RolloutStatus;
  rolloutUserGroupIds: string[];
}

const DEFAULT_FORM_VALUES: FeatureFlagFormValues = {
  name: "",
  description: "",
  rolloutStatus: "NoUsers",
  rolloutUserGroupIds: [],
};

export function FeatureFlagsPanel() {
  const flagsQuery = useFeatureFlags();
  const { data: flags = [], error } = flagsQuery;
  const { data: userGroups = [] } = useUserGroups();
  const createMutation = useCreateFeatureFlag();
  const updateMutation = useUpdateFeatureFlag();
  const addRolloutGroupsMutation = useAddFeatureFlagRolloutGroups();
  const removeRolloutGroupsMutation = useRemoveFeatureFlagRolloutGroups();
  const deleteMutation = useDeleteFeatureFlag();

  const [deletedDialogOpen, setDeletedDialogOpen] = React.useState(false);
  const deletedFlagsQuery = useDeletedFeatureFlags(deletedDialogOpen);
  const { data: deletedFlags = [], error: deletedError } = deletedFlagsQuery;
  const [restoreError, setRestoreError] = React.useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingFlag, setEditingFlag] =
    React.useState<FeatureFlagRecord | null>(null);
  const [deleteTarget, setDeleteTarget] =
    React.useState<FeatureFlagRecord | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const form = useForm<FeatureFlagFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onSubmit",
  });

  const userGroupOptions: UserGroupOption[] = React.useMemo(
    () =>
      userGroups.map((ug) => ({
        label: `Group: ${ug.name}`,
        value: ug.user_group_id,
      })),
    [userGroups],
  );

  const userGroupOptionById = React.useMemo(() => {
    const map = new Map<string, UserGroupOption>();
    for (const opt of userGroupOptions) {
      map.set(opt.value, opt);
    }
    return map;
  }, [userGroupOptions]);

  const sortedFlags = React.useMemo(() => {
    return [...flags].sort((a, b) => {
      return (
        featureFlagOrder.indexOf(a.rollout_status) -
        featureFlagOrder.indexOf(b.rollout_status)
      );
    });
  }, [flags]);

  function openCreate() {
    setEditingFlag(null);
    form.reset(DEFAULT_FORM_VALUES);
    setDialogOpen(true);
  }

  function openEdit(flag: FeatureFlagRecord) {
    setEditingFlag(flag);
    form.reset({
      name: flag.name,
      description: flag.description ?? "",
      rolloutStatus: flag.rollout_status,
      rolloutUserGroupIds:
        flag.rollout_status === "Partial"
          ? [...flag.rollout_user_group_ids]
          : [],
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    const trimmedName = values.name.trim();
    const trimmedDescription = values.description.trim() || null;

    try {
      if (editingFlag) {
        // Apply scalar fields (description, rollout_status) via update(), and
        // reconcile the rollout-group set via the granular add/remove
        // endpoints so the wire shape matches the user's intent: we are
        // adding the groups they ticked and removing the groups they
        // unticked, not blindly overwriting whatever the server currently
        // holds. Each step is skipped when there is nothing to do, so a
        // save with no changes issues zero round-trips.
        const scalarUpdates: Parameters<
          typeof updateMutation.mutateAsync
        >[0]["updates"] = {};
        if (trimmedDescription !== editingFlag.description) {
          scalarUpdates.description = trimmedDescription;
        }
        if (values.rolloutStatus !== editingFlag.rollout_status) {
          scalarUpdates.rollout_status = values.rolloutStatus;
        }
        if (Object.keys(scalarUpdates).length > 0) {
          await updateMutation.mutateAsync({
            featureFlagId: editingFlag.feature_flag_id,
            updates: scalarUpdates,
          });
        }

        const existingGroupIds = new Set(editingFlag.rollout_user_group_ids);
        const desiredGroupIds = new Set(values.rolloutUserGroupIds);
        const toAdd = values.rolloutUserGroupIds.filter(
          (id) => !existingGroupIds.has(id),
        );
        const toRemove = editingFlag.rollout_user_group_ids.filter(
          (id) => !desiredGroupIds.has(id),
        );
        if (toAdd.length > 0) {
          await addRolloutGroupsMutation.mutateAsync({
            featureFlagId: editingFlag.feature_flag_id,
            request: { user_group_ids: toAdd },
          });
        }
        if (toRemove.length > 0) {
          await removeRolloutGroupsMutation.mutateAsync({
            featureFlagId: editingFlag.feature_flag_id,
            request: { user_group_ids: toRemove },
          });
        }
      } else {
        await createMutation.mutateAsync({
          name: trimmedName,
          description: trimmedDescription,
          rollout_status: values.rolloutStatus,
          rollout_user_group_ids: values.rolloutUserGroupIds,
        });
      }
      closeDialog();
    } catch (err) {
      // Any failure in the create/update sequence is surfaced as a form-
      // level error rendered in the Alert at the top of the dialog; the
      // dialog stays open so the admin can correct and resubmit. A 409
      // from create is reworded to name the conflicting flag explicitly
      // since the raw server message is not actionable.
      let message: string;
      if (err instanceof RobotoConflictException) {
        message = `A feature flag with the name "${trimmedName}" already exists.`;
      } else if (err instanceof Error) {
        message = err.message;
      } else {
        message = "An error occurred.";
      }
      form.setError("root", { type: "server", message });
    }
  });

  function rolloutLabel(flag: FeatureFlagRecord): string {
    if (flag.rollout_status === "AllUsers") {
      return "All Users";
    }
    if (flag.rollout_status === "NoUsers") {
      return "No Users";
    }
    if (flag.rollout_user_group_ids.length === 0) {
      return "Partial";
    }
    const labels = flag.rollout_user_group_ids.map(
      (id) => userGroupOptionById.get(id)?.label ?? `Group: ${id}`,
    );
    if (labels.length <= ROLLOUT_LABEL_GROUP_THRESHOLD) {
      return labels.join(", ");
    }
    const shown = labels.slice(0, ROLLOUT_LABEL_GROUP_THRESHOLD).join(", ");
    const remaining = labels.length - ROLLOUT_LABEL_GROUP_THRESHOLD;
    return `${shown}, +${remaining} more`;
  }

  // The memoized columns call the latest handlers through refs, so they
  // needn't rebuild on every render.
  const rolloutLabelRef = React.useRef(rolloutLabel);
  rolloutLabelRef.current = rolloutLabel;
  const openEditRef = React.useRef(openEdit);
  openEditRef.current = openEdit;

  // Columns start hidden under DataTable's `defaultColDef.hide: true`;
  // `buildColumnsForPreferences` shows the default ids, and the actions
  // column carries its own `hide: false`. There is no column picker here, so
  // columns aren't movable: one dragged out could not be brought back.
  const columns = React.useMemo<ColDef<FeatureFlagRecord>[]>(
    () =>
      buildColumnsForPreferences<FeatureFlagRecord>({
        staticColumns: [
          ...flagColumns(rolloutLabelRef),
          {
            colId: "actions",
            headerName: "Actions",
            width: 200,
            // Pinned so the row's actions stay in view however narrow the
            // grid; `lockVisible` exempts the column from the visibility
            // overlay, which only knows the preference columns.
            pinned: "right",
            hide: false,
            lockVisible: true,
            resizable: false,
            suppressMovable: true,
            cellRenderer: (params: ICellRendererParams<FeatureFlagRecord>) => {
              const flag = params.data;
              if (!flag) {
                return null;
              }
              return (
                <>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openEditRef.current(flag)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteTarget(flag);
                    }}
                  >
                    Delete
                  </Button>
                </>
              );
            },
          },
        ],
        dynamicColumns: [],
        preferenceOrder: DEFAULT_VISIBLE_COLUMN_IDS,
      }),
    [],
  );

  const deletedColumns = React.useMemo<ColDef<FeatureFlagRecord>[]>(
    () =>
      buildColumnsForPreferences<FeatureFlagRecord>({
        staticColumns: [
          ...flagColumns(rolloutLabelRef),
          {
            colId: "actions",
            headerName: "Actions",
            width: 130,
            pinned: "right",
            hide: false,
            lockVisible: true,
            resizable: false,
            suppressMovable: true,
            cellRenderer: (params: ICellRendererParams<FeatureFlagRecord>) => {
              const flag = params.data;
              if (!flag) {
                return null;
              }
              return (
                <RestoreFlagButton flag={flag} onError={setRestoreError} />
              );
            },
          },
        ],
        dynamicColumns: [],
        preferenceOrder: DELETED_VISIBLE_COLUMN_IDS,
      }),
    [],
  );

  const table = useTable<FeatureFlagRecord>({
    getRowId: (row) => row.feature_flag_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });
  const deletedTable = useTable<FeatureFlagRecord>({
    getRowId: (row) => row.feature_flag_id,
    defaultVisibleColumnIds: DELETED_VISIBLE_COLUMN_IDS,
  });

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    addRolloutGroupsMutation.isPending ||
    removeRolloutGroupsMutation.isPending;
  const submitLabel = editingFlag ? "Save" : "Create";
  const serverError = form.formState.errors.root?.message;

  return (
    <div>
      <div className={styles.toolbar}>
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.explanation}
        >
          {explanation.trim()}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add Feature Flag
        </Button>
      </div>

      {error && (
        <Alert severity="error" className={styles.alert}>
          {error.message}
        </Alert>
      )}

      <ClientPagedTable
        table={table}
        columns={columns}
        rows={sortedFlags}
        loading={deriveLoadingState(flagsQuery)}
        noRowsContent="No feature flags yet."
      />

      <Button
        variant="text"
        size="small"
        className={styles.viewDeletedButton}
        onClick={() => {
          setRestoreError(null);
          setDeletedDialogOpen(true);
        }}
      >
        View Deleted Flags
      </Button>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFlag ? "Edit Feature Flag" : "Add Feature Flag"}
        </DialogTitle>
        <form
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          noValidate
        >
          <DialogContent className={styles.dialogContent}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            <TextField
              label="Name"
              required
              fullWidth
              /* the flag should not be renameable after it is created. */
              disabled={editingFlag !== null}
              error={Boolean(form.formState.errors.name)}
              helperText={form.formState.errors.name?.message}
              {...form.register("name", {
                validate: (value) =>
                  value.trim().length > 0 || "Name is required.",
              })}
            />
            <TextField
              label="Description"
              fullWidth
              {...form.register("description")}
            />
            <Controller
              control={form.control}
              name="rolloutStatus"
              render={({ field }) => (
                <FormControl component="fieldset">
                  <FormLabel component="legend">Rollout Status</FormLabel>
                  <RadioGroup
                    row
                    aria-label="Rollout Status"
                    value={field.value}
                    onChange={(e) => {
                      const next = e.target.value as RolloutStatus;
                      field.onChange(next);
                      // Drop any selected groups when the admin moves
                      // away from Partial so the form payload matches
                      // what we will send. This lives in the radio's
                      // onChange (not a useEffect on rolloutStatus) so
                      // it only fires on an admin's flip -- a useEffect
                      // would also fire when openEdit prefills the form
                      // and erase the groups we just loaded.
                      if (next !== "Partial") {
                        form.setValue("rolloutUserGroupIds", [], {
                          shouldDirty: true,
                        });
                        form.clearErrors("rolloutUserGroupIds");
                      }
                    }}
                  >
                    <FormControlLabel
                      value="NoUsers"
                      control={<Radio />}
                      label="No Users"
                    />
                    <FormControlLabel
                      value="Partial"
                      control={<Radio />}
                      label="Partial"
                    />
                    <FormControlLabel
                      value="AllUsers"
                      control={<Radio />}
                      label="All Users"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <RolloutGroupsField
              control={form.control}
              userGroupOptions={userGroupOptions}
              userGroupOptionById={userGroupOptionById}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} type="button">
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={isPending}>
              {isPending ? <CircularProgress size="1rem" /> : submitLabel}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {deleteError && (
        <Alert
          severity="error"
          className={styles.alert}
          onClose={() => setDeleteError(null)}
        >
          {deleteError}
        </Alert>
      )}

      <AlertDialog
        dialogOpen={deleteTarget !== null}
        handleClose={() => setDeleteTarget(null)}
        dialogTitle="Delete Feature Flag"
        dialogText={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"?`}
        dialogAction={async () => {
          if (!deleteTarget) {
            return;
          }
          try {
            await deleteMutation.mutateAsync(deleteTarget.feature_flag_id);
          } catch (err) {
            setDeleteError(
              err instanceof Error
                ? err.message
                : "Failed to delete feature flag.",
            );
          }
        }}
      />

      <Dialog
        open={deletedDialogOpen}
        onClose={() => setDeletedDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Deleted Feature Flags</DialogTitle>
        <DialogContent>
          <p>
            Deleted feature flags will be treated as fully disabled. They should
            not be recycled in code again.
          </p>
          <p>If you deleted a flag by mistake, you can restore it here.</p>
          <br />
          {restoreError && (
            <Alert
              severity="error"
              className={styles.alert}
              onClose={() => setRestoreError(null)}
            >
              {restoreError}
            </Alert>
          )}
          {deletedError && (
            <Alert severity="error">{deletedError.message}</Alert>
          )}
          <ClientPagedTable
            table={deletedTable}
            columns={deletedColumns}
            rows={deletedFlags}
            loading={deriveLoadingState(deletedFlagsQuery)}
            noRowsContent="No deleted feature flags."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

/**
 * The name, description and rollout columns, shared by the active and deleted
 * flag tables. Fully rolled-out flags are de-emphasized: they are done and
 * waiting to be cleaned up.
 */
function flagColumns(
  rolloutLabelRef: React.RefObject<(flag: FeatureFlagRecord) => string>,
): ColDef<FeatureFlagRecord>[] {
  const cellClass = (params: { data?: FeatureFlagRecord }) =>
    classNames(cellStyles.secondaryContent, {
      [styles.deemphasized]: params.data?.rollout_status === "AllUsers",
    });
  return [
    columnHelper.accessor("name", {
      headerName: "Name",
      flex: 1,
      suppressMovable: true,
      cellClass,
      cellRenderer: (params: ICellRendererParams<FeatureFlagRecord>) =>
        params.data ? (
          <span className={styles.nameCell}>
            <RolloutStatusIcon status={params.data.rollout_status} />
            {params.data.name}
          </span>
        ) : null,
    }),
    columnHelper.accessor("description", {
      headerName: "Description",
      flex: 2,
      suppressMovable: true,
      cellClass,
      cellRenderer: renderDashOrValue,
    }),
    {
      colId: "rollout",
      headerName: "Rollout",
      flex: 1,
      suppressMovable: true,
      cellClass,
      valueGetter: (params) =>
        params.data ? rolloutLabelRef.current?.(params.data) : "",
    },
  ];
}

function RolloutStatusIcon({ status }: { status: RolloutStatus }) {
  if (status === "AllUsers") {
    return <CircleCheckIcon fontSize="small" className={styles.statusIcon} />;
  }
  if (status === "Partial") {
    return (
      <CircleFadingArrowUpIcon fontSize="small" className={styles.statusIcon} />
    );
  }
  return <CircleDashedIcon fontSize="small" className={styles.statusIcon} />;
}

function RestoreFlagButton({
  flag,
  onError,
}: {
  flag: FeatureFlagRecord;
  onError: (message: string) => void;
}) {
  const restoreMutation = useRestoreFeatureFlag();
  return (
    <Button
      size="small"
      startIcon={<RestoreIcon />}
      disabled={restoreMutation.isPending}
      onClick={() => {
        void (async () => {
          try {
            await restoreMutation.mutateAsync(flag.feature_flag_id);
          } catch (err) {
            onError(
              err instanceof Error
                ? err.message
                : "Failed to restore feature flag.",
            );
          }
        })();
      }}
    >
      Restore
    </Button>
  );
}

/**
 * A {@link DataTable} over a list already held in full, paged client-side.
 * Both flag endpoints return every row in one response.
 */
function ClientPagedTable({
  table,
  columns,
  rows,
  loading,
  noRowsContent,
}: {
  table: Table<FeatureFlagRecord>;
  columns: ColDef<FeatureFlagRecord>[];
  rows: FeatureFlagRecord[];
  loading: React.ComponentProps<typeof DataTable>["loading"];
  noRowsContent: React.ReactNode;
}) {
  const { currentPage, pageSize } = table;
  const pageRows = React.useMemo(() => {
    const start = currentPage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);
  return (
    <div className={styles.tableSlot}>
      <DataTable<FeatureFlagRecord>
        table={table}
        columns={columns}
        data={pageRows}
        hasNextPage={(currentPage + 1) * pageSize < rows.length}
        totalRowCount={rows.length}
        loading={loading}
        noRowsContent={noRowsContent}
      />
    </div>
  );
}

interface RolloutGroupsFieldProps {
  control: Control<FeatureFlagFormValues>;
  userGroupOptions: UserGroupOption[];
  userGroupOptionById: Map<string, UserGroupOption>;
}

/**
 * Multi-select for the user groups a Partial flag rolls out to.
 *
 * The Autocomplete is disabled unless `rolloutStatus` is `"Partial"`, so
 * the admin sees the field but cannot pick groups for No Users / All Users
 * flags. `rolloutStatus` is read with `useWatch` rather than threaded in
 * as a prop so a radio toggle in the parent only re-renders this subtree
 * instead of the whole panel. See https://react-hook-form.com/docs/usewatch.
 */
function RolloutGroupsField({
  control,
  userGroupOptions,
  userGroupOptionById,
}: RolloutGroupsFieldProps) {
  const rolloutStatus = useWatch({ control, name: "rolloutStatus" });
  const groupSelectDisabled = rolloutStatus !== "Partial";

  return (
    <Controller
      control={control}
      name="rolloutUserGroupIds"
      render={({ field, fieldState }) => {
        const selectedOptions = field.value.map(
          (id) => userGroupOptionById.get(id) ?? { label: id, value: id },
        );
        return (
          <Autocomplete<UserGroupOption, true, false, false>
            multiple
            disableCloseOnSelect
            disabled={groupSelectDisabled}
            options={userGroupOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={selectedOptions}
            onChange={(_, newValue) => {
              field.onChange(newValue.map((v) => v.value));
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((opt, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={opt.label}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Groups"
                placeholder={
                  groupSelectDisabled
                    ? "Available when rollout status is Partial"
                    : "Pick one or more user groups…"
                }
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        );
      }}
    />
  );
}
